import React, { useEffect, useState } from 'react';
import { adminApi } from '../utils/adminApi';
import { useFirmSettings } from '../contexts/FirmSettingsContext';


function lastName(name) {
  const parts = (name || '').trim().split(/\s+/);
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : parts[0].toLowerCase();
}

export default function AdminMassEmailPage() {
  const { firm } = useFirmSettings();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.listClients().
    then((data) => {
      setClients([...data].sort((a, b) => lastName(a.name).localeCompare(lastName(b.name))));
      setLoading(false);
    }).
    catch(() => setLoading(false));
  }, []);

  // Search runs against the real underlying values so it still works while
  // privacy mode is on — what you type in the search box still finds the
  // right people, even though the displayed names are masked.
  const filtered = clients.filter((c) =>
  c.name.toLowerCase().includes(search.toLowerCase()) ||
  c.email?.toLowerCase().includes(search.toLowerCase()) ||
  c.username?.toLowerCase().includes(search.toLowerCase())
  );

  function toggleAll(checked) {
    setSelected(checked ? new Set(filtered.map((c) => c.id)) : new Set());
  }

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function handleSend(e) {
    e.preventDefault();
    if (!selected.size) return setError('Select at least one recipient.');
    setSending(true);setStatus('');setError('');
    try {
      // Send the REAL email + REAL name to the server — never masked values.
      // Mass-emailing in privacy mode would still deliver to real inboxes.
      const selectedClients = clients.filter((c) => selected.has(c.id));
      const result = await adminApi.sendMassEmail(subject, body, selectedClients.map((c) => ({ email: c.email, name: c.name })));
      setStatus(`Email sent to ${selected.size} client${selected.size !== 1 ? 's' : ''}.`);
      setSubject('');setBody('');setSelected(new Set());
    } catch (err) {setError(err.message);} finally
    {setSending(false);}
  }

  return (
    <div>
      <h1 className="page-title">Mass Email</h1>
      <p className="page-subtitle">
        Select the clients you want to email. Each recipient is BCC'd — they cannot see each other.
        You are in the To field.
      </p>

      {status && <div className="alert alert-success">{status}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Recipients selector */}
      <div className="section-head" style={{ marginTop: 0 }}>
        Select Recipients
        {selected.size > 0 && <span style={{ marginLeft: 8, fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>({selected.size} selected)</span>}
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="text" placeholder="Search clients..." value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 0, margin: 0, padding: '8px 10px', border: '1px solid #ddd', borderRadius: 3, fontSize: 13, fontFamily: 'Arial, sans-serif' }} />
          {search && <button className="btn btn-sm" onClick={() => setSearch('')}>Clear</button>}
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-sm" onClick={() => toggleAll(true)}>Select All{search ? ' Filtered' : ''}</button>
          <button className="btn btn-sm" onClick={() => setSelected(new Set())}>Deselect All</button>
        </div>

        {loading ?
        <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>Loading...</div> :

        <div style={{ border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden', maxHeight: 300, overflowY: 'auto' }}>
            {filtered.length === 0 &&
          <div style={{ padding: '16px', textAlign: 'center', fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888' }}>No clients found.</div>
          }
            {filtered.map((c, i) =>
          <div key={c.id}
          onClick={() => toggle(c.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer',
            background: selected.has(c.id) ? '#f0f4ff' : i % 2 === 0 ? '#fff' : '#fafafa',
            borderBottom: '1px solid #eee'
          }}>
                <input type="checkbox" checked={selected.has(c.id)}
            onChange={() => toggle(c.id)} onClick={(e) => e.stopPropagation()}
            style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: selected.has(c.id) ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.name}
                  </div>
                  <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: c.email?.includes('@pending.import') ? '#e67e22' : '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.email?.includes('@pending.import') ? '⚠ No email' : c.email || '—'}
                  </div>
                </div>
              </div>
          )}
          </div>
        }
      </div>

      {/* Compose */}
      <div className="section-head">Compose Email</div>
      <div className="card">
        <form onSubmit={handleSend}>
          <div className="form-group">
            <label>Subject</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
            placeholder={`e.g. Your Client Portal Login — ${firm.firm_name || 'Your Firm'}`} required />
          </div>
          <div className="form-group">
            <label>Message</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)}
            style={{ minHeight: 200 }}
            placeholder={`Dear Client,\n\nYour secure client portal is ready...\n\nSincerely,\n${firm.firm_name || 'Your Firm'}`}
            required />
          </div>
          <p style={{ fontSize: 12, fontFamily: 'Arial, sans-serif', color: '#888', marginBottom: 12 }}>
            Your firm name, address, and contact info are added automatically to the bottom.
            Clients with ⚠ No email will be skipped.
          </p>
          <button type="submit" className="btn btn-primary" disabled={sending || !selected.size}>
            {sending ?
            <><span className="spinner" />Sending...</> :
            `Send to ${selected.size} Client${selected.size !== 1 ? 's' : ''}`}
          </button>
        </form>
      </div>
    </div>);

}